import React, { Dispatch, useEffect, useRef, useState } from 'react';

import {
    PaperPlanMetadata,
    PlayType,
    ProgramType,
    RatchetModels
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';

import { isEmpty } from 'lodash';
import { AddAdRevShare } from 'src/components/AddPrimeAMFRateCard/AddContractTerms/AddAdRevShare';
import { ROUTES } from 'src/constants/routes';
import { IMetadata } from 'src/types/IMetadata';
import { IPrimeAMFRateCardValidationAction } from 'src/types/IPrimeAMFRateCardValidationAction';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';

import { IPrimeAMFRateCardErrors } from '../../rateCardValidationReducer';
import { AddConversionModel } from '../AddConversionModel';
import { AddCustomerType } from '../AddCustomerType';
import { AddPlayType } from '../AddPlayType';
import { AddProgramType } from '../AddProgramType';
import { AddCapPRSDeduction } from './AddCapPRSDeduction';

interface IAddCostPerPlay {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    rateCardErrors: IPrimeAMFRateCardErrors;
    dispatchRateCardErrors: Dispatch<IPrimeAMFRateCardValidationAction>;
    playTypes: PlayType[];
    customerTypes: PaperPlanMetadata[];
    programTypes: ProgramType[];
    conversionModels: RatchetModels;
    augmentedMetadata: IMetadata;
}

export const AddCostPerPlay: React.FC<IAddCostPerPlay> = ({
    rateCardValues,
    dispatchRateCardValues,
    rateCardErrors,
    dispatchRateCardErrors,
    playTypes,
    customerTypes,
    programTypes,
    conversionModels,
    augmentedMetadata
}) => {
    const firstMount = useRef(true);
    const [isToggled, setIsToggled] = useState(
        [
            rateCardValues.primeContractTerms?.costPerPlay?.playTypes,
            rateCardValues.primeContractTerms?.costPerPlay?.customerTypes,
            rateCardValues.primeContractTerms?.costPerPlay?.programTypeList,
            rateCardValues.primeContractTerms?.costPerPlay?.conversions
        ].some((attr) => !isEmpty(attr))
    );
    const errorMessage = rateCardErrors.primeContractTerms.costPerPlay.model;

    // Validate on rate card change
    useEffect(() => {
        // Skip validation on mount to avoid displaying initial error messsage
        if (firstMount.current) {
            firstMount.current = false;
            return;
        }

        dispatchRateCardErrors({
            type: 'validate_cost_per_play',
            rateCardValues,
            isToggled
        });
    }, [rateCardValues.primeContractTerms]);

    const handleToggle = (): void => {
        if (isToggled) {
            dispatchRateCardValues({ type: 'reset_cost_per_play' });
        } else {
            dispatchRateCardErrors({
                type: 'validate_is_next_disabled',
                rateCardValues,
                stepId: ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS
            });
        }

        setIsToggled((prevIsToggled) => !prevIsToggled);
    };

    return (
        <Column>
            <Row alignmentHorizontal="justify">
                <Text type="h500">Cost per play model</Text>
                <Toggle checked={isToggled} onChange={handleToggle} />
            </Row>
            {isToggled && (
                <>
                    {!isEmpty(errorMessage) && (
                        <Alert type="error" size="medium">
                            {errorMessage}
                        </Alert>
                    )}
                    <AddPlayType
                        rateCardValues={rateCardValues}
                        dispatchRateCardValues={dispatchRateCardValues}
                        rateCardErrors={rateCardErrors}
                        dispatchRateCardErrors={dispatchRateCardErrors}
                        playTypes={playTypes}
                    />
                    <AddCustomerType
                        rateCardValues={rateCardValues}
                        dispatchRateCardValues={dispatchRateCardValues}
                        rateCardErrors={rateCardErrors}
                        dispatchRateCardErrors={dispatchRateCardErrors}
                        customerTypes={customerTypes}
                        isMAU={false}
                    />
                    <AddProgramType
                        rateCardValues={rateCardValues}
                        dispatchRateCardValues={dispatchRateCardValues}
                        rateCardErrors={rateCardErrors}
                        dispatchRateCardErrors={dispatchRateCardErrors}
                        programTypes={programTypes}
                        augmentedMetadata={augmentedMetadata}
                    />
                    <AddCapPRSDeduction
                        rateCardValues={rateCardValues}
                        dispatchRateCardValues={dispatchRateCardValues}
                        rateCardErrors={rateCardErrors}
                        dispatchRateCardErrors={dispatchRateCardErrors}
                    />
                    <AddConversionModel
                        rateCardValues={rateCardValues}
                        dispatchRateCardValues={dispatchRateCardValues}
                        conversionModels={conversionModels}
                    />
                    <AddAdRevShare
                        rateCardValues={rateCardValues}
                        dispatchRateCardValues={dispatchRateCardValues}
                        rateCardErrors={rateCardErrors}
                        dispatchRateCardErrors={dispatchRateCardErrors}
                        playTypes={playTypes}
                        conversionModels={conversionModels}
                        augmentedMetadata={augmentedMetadata}
                    />
                </>
            )}
        </Column>
    );
};
